import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Users, Building2, BarChart3, UserCheck, Clock, AlertTriangle, XCircle, ArrowRight } from 'lucide-react';

const MasterAdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api('/master-admin/stats');
        if (res.ok) setStats(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const card = (label, value, icon, color, sub) => (
    <div className="ma-stat-card" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="ma-stat-label">{label}</div>
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {React.cloneElement(icon, { size: 18, color })}
        </div>
      </div>
      <div className="ma-stat-value" style={{ color }}>{loading ? '—' : value}</div>
      {sub && <div style={{ fontSize: '12px', color: 'var(--ma-text-muted)' }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="ma-header">
        <h1>System Overview</h1>
        <p style={{ fontSize: '14px', color: 'var(--ma-text-muted)', marginTop: '4px' }}>
          Global platform metrics across all tenants
        </p>
      </div>

      {/* ── Main stats ── */}
      <div className="ma-stats-grid" style={{ marginBottom: '24px' }}>
        {card('Super Admins',   stats?.totalSuperAdmins ?? 0, <Users />,      '#3b82f6', 'Total registered')}
        {card('Active Tenants', stats?.totalTenants ?? 0,     <Building2 />,  '#10b981', 'Unique workspaces')}
        {card('Total Leads',    stats?.totalLeads ?? 0,        <BarChart3 />,  '#8b5cf6', 'Across all tenants')}
        {card('All Users',      stats?.totalUsers ?? 0,        <UserCheck />,  '#f59e0b', 'Including all roles')}
      </div>

      {/* ── Subscription overview ── */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700' }}>Subscription Overview</h2>
          <button
            onClick={() => navigate('/master-admin/subscriptions')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '7px 14px', borderRadius: '8px',
              background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)',
              color: '#60a5fa', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
            }}
          >
            View All <ArrowRight size={13} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
          {[
            { label: 'Active Trials',  value: stats?.subscriptions?.activeTrials  ?? 0, color: '#10b981', icon: <Clock size={16} />,         bg: 'rgba(16,185,129,0.1)' },
            { label: 'Expiring Soon',  value: stats?.subscriptions?.expiringSoon  ?? 0, color: '#f59e0b', icon: <AlertTriangle size={16} />,  bg: 'rgba(245,158,11,0.1)' },
            { label: 'Expired',        value: stats?.subscriptions?.expiredTrials ?? 0, color: '#ef4444', icon: <XCircle size={16} />,         bg: 'rgba(239,68,68,0.1)'  },
          ].map((s, i) => (
            <button
              key={i}
              onClick={() => navigate('/master-admin/subscriptions')}
              className="ma-stat-card"
              style={{ display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer', textAlign: 'left', border: `1px solid ${s.color}22` }}
            >
              <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: s.color }}>
                {s.icon}
              </div>
              <div>
                <div style={{ fontSize: '22px', fontWeight: '800', color: s.color }}>{loading ? '—' : s.value}</div>
                <div style={{ fontSize: '12px', color: 'var(--ma-text-muted)' }}>{s.label}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Activity placeholder ── */}
      <div>
        <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '14px' }}>Recent Activity</h2>
        <div className="ma-stat-card" style={{ minHeight: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: 'var(--ma-text-muted)', fontSize: '14px' }}>System logs and activity will appear here...</p>
        </div>
      </div>
    </div>
  );
};

export default MasterAdminDashboard;
